import { t } from "@lingui/macro"
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    FormHelperText,
    SelectChangeEvent,
} from "@mui/material"
import { observer } from "mobx-react"
import { useCallback } from "react"

import { useStore } from "src/store/lib/useStore"
import { AlertDetailModalStore } from "src/modals/alert-detail/store"
import { AccessGroup } from "src/components/ModalAccessGroup/AccessGroup"
import { SegmentPickerField } from "src/components/SegmentPickerField"
import { ALERT_LEVEL_OPTIONS, AlertLevel } from "src/views/alerts/constants"
import { AlertLevelChip } from "src/views/alerts/components/AlertLevelChip"

export const AlertSettingsSection = observer(() => {
    const store = useStore(AlertDetailModalStore)

    const handleAccessGroupChange = useCallback(
        (accessGroupId: number) => {
            store.formFields.set("access_group_id", accessGroupId)
        },
        [store.formFields],
    )

    const handleSegmentsChange = useCallback(
        (segmentIds: number[]) => {
            store.selectedSegments = segmentIds
        },
        [store],
    )

    const handleLevelChange = useCallback(
        (event: SelectChangeEvent<AlertLevel>) => {
            store.formFields.set("level", event.target.value as AlertLevel)
        },
        [store.formFields],
    )

    return (
        <>
            <AccessGroup
                disabled={store.isReadOnly}
                accessGroupId={store.formFields.get("access_group_id")}
                errorMessage={store.formFields.error("access_group_id") ?? ""}
                onChange={handleAccessGroupChange}
            />
            <FormControl sx={{ width: "100%", mt: 2 }}>
                <InputLabel id="alert-level-label">
                    {t`alerts-modal.level-label`}
                </InputLabel>
                <Select
                    labelId="alert-level-label"
                    id="alert-level"
                    value={store.formFields.get("level")}
                    label={t`alerts-modal.level-label`}
                    onChange={handleLevelChange}
                    disabled={store.isReadOnly}
                    error={Boolean(store.formFields.error("level"))}
                    data-testid="alert-level-select"
                    renderValue={(value) => (
                        <AlertLevelChip level={value as AlertLevel} />
                    )}
                >
                    {ALERT_LEVEL_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            <AlertLevelChip level={option.value} />
                        </MenuItem>
                    ))}
                </Select>
                {Boolean(store.formFields.error("level")) && (
                    <FormHelperText error={true}>
                        {store.formFields.error("level")}
                    </FormHelperText>
                )}
            </FormControl>
            <SegmentPickerField
                value={store.selectedSegments}
                onChange={handleSegmentsChange}
                accessGroupId={store.formFields.get("access_group_id")}
                disabled={store.isReadOnly}
            />
        </>
    )
})
